import { useEffect, useRef } from "react";
import L from "leaflet";
import { Crosshair, Map as MapIcon } from "lucide-react";
import { RISK, GULLY } from "@/lib/constants";
import { SAT_COLORS } from "@/components/SatellitePanel";

const BARBADOS = [13.1783, -59.5432];
const ISLAND_BOUNDS = [[13.03, -59.66], [13.34, -59.41]];
const CONE_STYLE = { color: "#e2e8f0", weight: 1.2, opacity: 0.8, dashArray: "4 4",
  fillColor: "#f8fafc", fillOpacity: 0.1, interactive: false };

export default function TacticalMap({ assets, visibleTiers, visibleTypes, onSelect, baseLayer,
  gullies, inundation, showGullies, showInundation, isolatedIds, storm, stormHour, stormActive,
  gullySegments, rainFlagged, nhcStorms, frameTarget, closedIds, satellite, controlsRight = 12, controlsTop = 12, roomView = null }) {
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const overlayRef = useRef(null);
  const stormRef = useRef(null);
  const satRef = useRef(null);
  const pulseRef = useRef(null);
  const nhcRef = useRef(null);
  const gullyRef = useRef(null);
  const framedKey = useRef(null);
  const baseRef = useRef({});
  const containerRef = useRef(null);

  // init map once
  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: BARBADOS,
      zoom: 12,
      zoomControl: false,
      attributionControl: true,
      preferCanvas: true,
    });

    const esri = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 19, attribution: "Imagery © Esri" }
    );
    const dark = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 16, attribution: "© Esri Dark Gray Canvas" }
    );
    const labels = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 19, pane: "shadowPane", opacity: 0.9 }
    );
    L.control.zoom({ position: "bottomright" }).addTo(map);
    baseRef.current = { esri, dark, labels };
    esri.addTo(map);
    labels.addTo(map);

    satRef.current = L.layerGroup().addTo(map);
    overlayRef.current = L.layerGroup().addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    map.createPane("gullyPane").style.zIndex = 450;
    gullyRef.current = L.layerGroup().addTo(map);
    nhcRef.current = L.layerGroup().addTo(map);
    stormRef.current = L.layerGroup().addTo(map);
    const rp = map.createPane("radarPane");
    rp.style.zIndex = 350; rp.style.pointerEvents = "none";
    L.marker(BARBADOS, { pane: "radarPane", interactive: false, keyboard: false,
      icon: L.divIcon({ className: "radar-icon", iconSize: [1400, 1400],
        html: '<div class="radar-rings"></div><div class="radar-sweep"></div>' }) }).addTo(map);
    const pp = map.createPane("pulsePane");
    pp.style.zIndex = 640; pp.style.pointerEvents = "none";
    pulseRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 250);
  }, []);

  // swap base layer
  useEffect(() => {
    const map = mapRef.current;
    const b = baseRef.current;
    if (!map || !b.esri) return;
    map.removeLayer(b.esri);
    map.removeLayer(b.dark);
    if (baseLayer === "dark") {
      b.dark.addTo(map);
    } else {
      b.esri.addTo(map);
    }
    b.labels.bringToFront();
  }, [baseLayer]);

  // render assets
  useEffect(() => {
    const map = mapRef.current;
    const group = layerRef.current;
    if (!map || !group) return;
    group.clearLayers();
    const pulses = pulseRef.current;
    pulses.clearLayers();
    let nPulse = 0;

    assets.forEach((a) => {
      if (!visibleTiers.has(a.tier)) return;
      if (!visibleTypes.has(a.asset_type)) return;
      const rc = RISK[a.tier];
      if ((a.tier === "critical" || a.tier === "high") && nPulse < 80) {
        nPulse += 1;
        L.marker(a.position, { pane: "pulsePane", interactive: false, keyboard: false,
          icon: L.divIcon({ className: "", iconSize: [34, 34],
            html: `<span class="risk-pulse" data-testid="risk-pulse" style="--c:${rc.color}"></span>` }) }).addTo(pulses);
      }
      const critical = a.tier === "critical";
      const closed = closedIds && closedIds.has(a.id);

      if (closed) {
        const geom = a.asset_type === "road" && a.path && a.path.length > 1;
        const lyr = geom
          ? L.polyline(a.path, { color: "#f8fafc", weight: 5, opacity: 1, dashArray: "6 5", pane: "gullyPane" })
          : L.circleMarker(a.position, { radius: 8, color: "#f8fafc", weight: 3, fillColor: "#0b1220", fillOpacity: 1, pane: "gullyPane" });
        lyr.on("click", () => onSelect(a));
        lyr.bindTooltip(`IMPASSABLE · ${a.name}`, { className: "closure-tip" });
        lyr.addTo(group);
        return;
      }

      if (a.asset_type === "road" && a.path && a.path.length > 1) {
        const line = L.polyline(a.path, {
          color: rc.color,
          weight: critical ? 4 : a.tier === "high" ? 3 : 2.2,
          opacity: 0.9,
          className: critical ? "risk-critical" : "",
        });
        line.on("click", () => onSelect(a));
        line.bindTooltip(popupHtml(a), { sticky: true, opacity: 1, className: "asset-tip" });
        line.addTo(group);
      } else {
        const radius = a.asset_type === "port" ? 8 : a.asset_type === "shelter" ? 6 : 5;
        const isolated = isolatedIds && isolatedIds.has(a.id);
        const gully = rainFlagged && rainFlagged.has(a.id);
        const marker = L.circleMarker(a.position, {
          radius: critical ? radius + 3 : radius,
          color: isolated ? "#FFFFFF" : gully ? GULLY.surcharge.color : rc.color,
          weight: isolated || gully ? 3 : 2,
          dashArray: isolated ? "3 3" : gully ? "1 3" : null,
          fillColor: rc.color,
          fillOpacity: 0.55,
          className: critical ? "risk-critical" : "",
        });
        marker.on("click", () => onSelect(a));
        marker.bindTooltip(popupHtml(a, isolated, gully), { direction: "top", opacity: 1, className: "asset-tip" });
        marker.addTo(group);
      }
    });
  }, [assets, visibleTiers, visibleTypes, onSelect, isolatedIds, rainFlagged, closedIds]);

  // overlays: inundation cells + gully flow network
  useEffect(() => {
    const map = mapRef.current;
    const ov = overlayRef.current;
    if (!map || !ov) return;
    ov.clearLayers();

    if (showInundation && inundation?.overlay) {
      L.imageOverlay(`${process.env.REACT_APP_BACKEND_URL}${inundation.overlay.url}`, inundation.overlay.bounds,
        { opacity: 0.85, interactive: false, className: "inundation-overlay" }).addTo(ov);
    }

    const gl = gullyRef.current;
    gl.clearLayers();
    const segs = gullySegments && gullySegments.length ? gullySegments : gullies || [];
    segs.forEach((g) => {
      const st = g.status || "normal";
      if (!showGullies && st === "normal") return;
      const hot = st === "overflow" || st === "surcharge";
      const line = L.polyline(g.path, {
        color: GULLY[st].color,
        weight: 1.5 + (g.weight || 0) * 3 + (hot ? 2 : 0),
        opacity: hot ? 0.95 : 0.6,
        className: st === "overflow" ? "risk-critical" : "",
        ...(hot ? { pane: "gullyPane" } : {}),
      }).addTo(hot ? gl : ov);
      if (g.ratio != null) {
        line.bindPopup(`<div style="font-family:Rajdhani;font-weight:700;color:${GULLY[st].color}">GULLY ${GULLY[st].label}</div><div style="font-family:JetBrains Mono;font-size:11px;color:#94a3b8;line-height:1.6">Load: ${g.ratio}× bank-full<br/>Q: ${g.q_m3s} / ${g.capacity_m3s} m³/s<br/>Catchment: ${g.catchment_km2} km²</div>`);
      }
    });
  }, [gullies, gullySegments, inundation, showGullies, showInundation]);

  // Sentinel-1 radar flood check: backdrop + detected water + validation markers
  useEffect(() => {
    const sg = satRef.current;
    if (!sg) return;
    sg.clearLayers();
    if (!satellite?.overlay) return;
    L.imageOverlay(`${process.env.REACT_APP_BACKEND_URL}${satellite.overlay.url}`, satellite.overlay.bounds,
      { opacity: 0.9, interactive: false, className: "satellite-overlay" }).addTo(sg);
    const v = satellite.validation;
    ["confirmed", "radar_only", "engine_only"].forEach((k) => (v?.[k] || []).forEach((a) => {
      L.circleMarker(a.position, { radius: 6, color: SAT_COLORS[k], weight: 2.5, fill: false })
        .bindTooltip(`<b>${a.name}</b><br/>${k.replace("_", " ")}`, { className: "asset-tip" }).addTo(sg);
    }));
  }, [satellite]);

  // official NHC cones + forecast points for real active systems
  useEffect(() => {
    const ng = nhcRef.current;
    if (!ng) return;
    ng.clearLayers();
    (nhcStorms || []).forEach((s) => {
      (s.cone || []).forEach((ring) => L.polygon(ring, CONE_STYLE).addTo(ng));
      const pts = (s.forecast_points || []).map((p) => [p.lat, p.lon]);
      if (pts.length > 1) L.polyline(pts, { color: "#f8fafc", weight: 1.5, opacity: 0.8, interactive: false }).addTo(ng);
      (s.forecast_points || []).forEach((p) => {
        L.circleMarker([p.lat, p.lon], { radius: 3.5, color: "#0f172a", weight: 1, fillColor: "#FBBF24", fillOpacity: 1 })
          .bindPopup(`<div style="font-family:JetBrains Mono;font-size:11px;color:#94a3b8">${s.name} +${p.hour}h<br/>${p.valid || ""}<br/>${p.wind_kt ? p.wind_kt + " kt" : ""}</div>`).addTo(ng);
      });
      L.circleMarker(s.position, { radius: 7, color: "#FFFFFF", weight: 2, fillColor: "#FF9F0A", fillOpacity: 0.95 })
        .bindPopup(`<div style="font-family:Rajdhani;font-weight:700;color:#f8fafc">${s.class_label} ${s.name}</div><div style="font-family:JetBrains Mono;font-size:11px;color:#94a3b8">${s.cone_source === "NHC_OFFICIAL" ? "NHC official cone · adv " + (s.advisory || "") : "Engine-projected cone"}<br/>${s.distance_km} km from Bridgetown</div>`)
        .addTo(ng);
    });
  }, [nhcStorms]);

  // auto-frame once per new storm/timeline
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !frameTarget || !frameTarget.auto || framedKey.current === frameTarget.key) return;
    framedKey.current = frameTarget.key;
    frameStorm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameTarget]);

  // storm track + eye + wind-field cone
  useEffect(() => {
    const map = mapRef.current;
    const sg = stormRef.current;
    if (!map || !sg) return;
    sg.clearLayers();
    if (!stormActive || !storm || !storm.track || !storm.track.length) return;

    const track = storm.track;
    const idx = Math.max(0, Math.min(track.length - 1, stormHour ?? 0));
    const pts = track.map((p) => [p.lat, p.lon]);
    const past = pts.slice(0, idx + 1);
    const future = pts.slice(idx);

    L.polyline(future, { color: "#f87171", weight: 2, opacity: 0.5, dashArray: "5 6", interactive: false }).addTo(sg);
    L.polyline(past, { color: "#FF3B30", weight: 2.5, opacity: 0.9, interactive: false }).addTo(sg);

    (storm.cone || []).forEach((ring) => L.polygon(ring, CONE_STYLE).addTo(sg));
    const eye = track[idx];
    const pos = [eye.lat, eye.lon];
    // replays: observed HURDAT2 34-kt wind radius at this hour; drills: synthetic core ring
    if (eye.r34_km) {
      L.circle(pos, { radius: eye.r34_km * 1000, color: "#FF9F0A", weight: 1, opacity: 0.7, fillColor: "#FF9F0A",
        fillOpacity: 0.08, interactive: false }).addTo(sg).bindTooltip("Observed 34-kt (63 km/h) wind radius");
    } else if (storm.radius_km) {
      L.circle(pos, { radius: storm.radius_km * 1000, color: "#FF9F0A",
        weight: 1, opacity: 0.6, fillColor: "#FF9F0A", fillOpacity: 0.08, interactive: false }).addTo(sg);
      L.circle(pos, { radius: storm.radius_km * 500, color: "#FF3B30",
        weight: 1, opacity: 0.5, fillColor: "#FF3B30", fillOpacity: 0.12, interactive: false }).addTo(sg);
    }
    // pulsing eye
    L.circleMarker(pos, { radius: 7, color: "#FFFFFF", weight: 2, fillColor: "#FF3B30",
      fillOpacity: 0.95, className: "risk-critical" }).addTo(sg)
      .bindPopup(`<div style="font-family:Rajdhani;font-weight:700;color:#f8fafc">${storm.name || "STORM"}</div><div style="font-family:JetBrains Mono;font-size:11px;color:#94a3b8">Cat ${storm.category} · ${eye.wind_kmh || "?"} km/h<br/>eye @ +${eye.hour}h</div>`);
  }, [storm, stormHour, stormActive]);

  function frameStorm() {
    const map = mapRef.current;
    if (!map || !frameTarget?.points?.length) return;
    const b = L.latLngBounds(frameTarget.points).extend(ISLAND_BOUNDS);
    map.flyToBounds(b, { padding: [40, 40], duration: 1.4, maxZoom: 9 });
  }
  const frameIsland = () => mapRef.current?.flyToBounds(ISLAND_BOUNDS, { duration: 1.2 });

  // command-room camera
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !roomView) return;
    const t = setTimeout(() => {
      map.invalidateSize();
      if (roomView === "storm") {
        if (frameTarget?.points?.length) frameStorm();
        else map.flyTo([14.5, -57], 5, { duration: 1.6 });
      } else frameIsland();
    }, 350);
    return () => clearTimeout(t);
  }, [roomView]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (roomView === null && mapRef.current) setTimeout(() => mapRef.current.invalidateSize(), 350);
  }, [roomView]);

  return (
    <div className="w-full h-full relative">
      <div ref={containerRef} data-testid="tactical-map" className="w-full h-full" />
      <div style={{ right: controlsRight, top: controlsTop }} className="room-hide absolute z-[550] flex flex-col gap-1.5 transition-[right,top] duration-300">
        <FrameBtn testid="map-frame-storm" onClick={frameStorm} disabled={!frameTarget} icon={Crosshair}
          label={frameTarget ? `Frame ${frameTarget.label}` : "No storm"} />
        <FrameBtn testid="map-frame-island" onClick={frameIsland} icon={MapIcon} label="Island" />
      </div>
    </div>
  );
}

function FrameBtn({ testid, onClick, disabled, icon: Icon, label }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className="glass flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border font-mono-t text-[10px] uppercase tracking-wide text-slate-200 transition-colors hover:border-cyan/60 hover:text-cyan disabled:opacity-40 disabled:cursor-not-allowed">
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function popupHtml(a, isolated, gully) {
  const rc = RISK[a.tier];
  const grid = a.national_e != null ? `BNG: ${a.national_e}E ${a.national_n}N` : "";
  return `
    <div style="font-family:Outfit,sans-serif;min-width:190px">
      <div style="font-family:Rajdhani;font-weight:700;font-size:15px;text-transform:uppercase;letter-spacing:.03em;color:#f8fafc">${a.name}</div>
      <div style="font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.08em;margin-bottom:8px">${a.asset_type} · ${a.category || ""}</div>
      ${isolated ? '<div style="font-size:11px;color:#FF3B30;font-weight:700;margin-bottom:6px">⚠ ISOLATED — no route to a live port</div>' : ""}
      ${gully ? '<div style="font-size:11px;color:#FF9F0A;font-weight:700;margin-bottom:6px">⚠ GULLY OVERFLOW ZONE — flash-flood risk</div>' : ""}
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
        <span style="display:inline-block;padding:2px 8px;border-radius:4px;font-family:JetBrains Mono;font-size:11px;font-weight:700;background:${rc.color}22;color:${rc.color};border:1px solid ${rc.color}">${rc.label}</span><span style="margin-left:6px;font-family:JetBrains Mono;font-size:11px;color:#94a3b8">${a.elevation_m ?? "—"} m ground · freeboard ${a.freeboard_m ?? "—"} m</span>
      </div>
      <div style="font-family:JetBrains Mono;font-size:11px;color:#94a3b8;line-height:1.6">
        Elevation: ${a.elevation_m} m<br/>
        Coast dist: ${a.coast_distance_m} m<br/>
        ${grid}
      </div>
    </div>`;
}
